import { addEventToDatalayer, addEventToGa4, addEventToLoop } from '../helpers';
import {
  productDataGTM,
  buildGa4Event,
  buildLoopEvent,
  amountTag,
  buildSelectContent,
  rechargeProductFields,
  bonusSlug,
} from '../utils';
import type { LoopClickType } from '../utils';
import { TRANSLATE_PAYMENT_METHOD } from '../../../constants/paymentMethods';
import { toKebabCase } from '../../../utils/formatters';
import { useRechargeStore } from '../../../store/recharge';
import { useCheckoutStore } from '../../../store/checkout';
import { isRecurrenceOptinAccepted } from '../../../utils/recurrence';
import type { RecurrenceOutcome } from '../../../types/recharge';

const formatPaymentMethod = (paymentMethod: string): string => {
  const paymentName = TRANSLATE_PAYMENT_METHOD[paymentMethod];
  return ['credit', 'debit'].includes(paymentMethod)
    ? `Cartão de ${paymentName}`
    : paymentName;
};

// Segmento da recarga programada nos labels de GA4 da adesão (documento
// "Bemobi - Projeto Recorrencia" da TIM, slides 6 e 7). `30dias` é fixo: o BFF
// define a periodicidade e não a envia no payload.
const GA4_PROGRAMADA = 'programada_30dias';
const GA4_NAO_PROGRAMADA = 'naoprogramada';

// O documento só define `naopossivel`; `ativa` e `processamento` seguem os
// precedentes (`programada-ativa` da Cadastra e o label `processamento` da tela
// "Falta pouco!") — decisão registrada na TPC-3835.
const GA4_RECURRENCE_STATUS: Record<RecurrenceOutcome, string> = {
  success: 'ativa',
  processing: 'processamento',
  error: 'naopossivel',
};

// Label da tela de sucesso. Fora do checkout a programada não existe, e o label
// segue o de sempre. No checkout ele descreve o que a tela mostra: programada
// quando a linha da adesão aparece — mesma condição do `buildRecurrenceItem` —
// e, nela, o desfecho da consulta.
const sucessoGa4Label = (): string => {
  const { rechargeResult, recurrenceResult } = useRechargeStore.getState();
  if (!rechargeResult?.fromCheckout) return 'recarga_efetuada_sucesso';

  const status = recurrenceResult
    ? GA4_RECURRENCE_STATUS[recurrenceResult.outcome]
    : undefined;
  return status
    ? `recarga_efetuada_sucesso_${GA4_PROGRAMADA}_${status}`
    : `recarga_efetuada_sucesso_${GA4_NAO_PROGRAMADA}`;
};

export const trackAnalyticsFastRecharge = (
  rechargeValue: unknown,
  transactionId: string,
) => {
  addEventToDatalayer({
    event: 'conversao',
    fluxo: 'repeat',
    pedido: transactionId,
    ...productDataGTM(rechargeValue),
    forma: formatPaymentMethod('credit'),
  });
  const amount = ((rechargeValue as any)?.id ?? (rechargeValue as any)) / 100;
  addEventToLoop(
    buildLoopEvent({
      type: 'confirmation',
      pageName: 'REPETIR_RECARGA',
      paymentMethod: 'credit',
      transactionAmount: amount,
    }),
  );
};

type UpsellClickType =
  | 'click_manter'
  | 'click_upsell'
  | 'click_premiada'
  | 'click_upsell+premiada';

type UpsellVariant = 'valor' | 'premiada' | 'combo' | 'controle';

export const trackAnalyticsUpsellClick = (
  type: UpsellClickType,
  amountCents: number,
  variant: UpsellVariant,
) => {
  addEventToLoop(
    buildLoopEvent({
      type,
      pageName: `upsell_modal_${variant}`,
      transactionAmount: amountCents / 100,
    }),
  );
};

// Modal sobre a tela de valores: impression, não pageview.
export const trackAnalyticsUpsellImpression = (
  variant: UpsellVariant,
  amountCents: number,
) => {
  addEventToLoop(
    buildLoopEvent({
      type: 'impression',
      pageName: `upsell_modal_${variant}`,
      transactionAmount: amountCents / 100,
    }),
  );
};

export const trackAnalyticsUpsellView = (
  upsellProduct: unknown,
  comm: string,
  ga4Label: string,
) => {
  addEventToDatalayer({
    event: 'oferta_upsell',
    comm,
    upList: [productDataGTM(upsellProduct)],
  });
  addEventToGa4(
    buildGa4Event({
      type: 'view',
      location: 'recarga_bemobi',
      label: ga4Label,
    }),
  );
};

export const trackAnalyticsUpsellSelect = (
  product: unknown,
  comm: string,
  ga4Label: string,
  ga4Value: string,
) => {
  addEventToDatalayer({
    event: 'selecionar_upsell',
    ...productDataGTM(product),
    comm,
  });
  addEventToGa4(
    buildGa4Event({
      type: 'click',
      location: 'recarga_bemobi',
      label: ga4Label,
      value: ga4Value,
    }),
  );
};

export const trackAnalyticsUpsellDecline = (
  ga4Label: string,
  ga4Value: string,
) => {
  addEventToGa4(
    buildGa4Event({
      type: 'click',
      location: 'recarga_bemobi',
      label: ga4Label,
      value: ga4Value,
    }),
  );
};

const UPSELL_CONTROL_GA4_LABEL = 'upsell_controle_express';

// action/url só existem nos eventos de upsell de controle (contrato específico
// da US 1449056) — não fazem parte do buildGa4Event genérico, usado por vários
// outros eventos sem relação nenhuma com essa demanda.
export const trackAnalyticsUpsellControlView = () => {
  addEventToGa4({
    ...buildGa4Event({
      type: 'view',
      location: 'recarga_bemobi',
      label: UPSELL_CONTROL_GA4_LABEL,
    }),
    action: '',
    url: '',
  });
};

export const trackAnalyticsUpsellControlDecline = () => {
  addEventToGa4({
    ...buildGa4Event({
      type: 'click',
      location: 'recarga_bemobi',
      label: UPSELL_CONTROL_GA4_LABEL,
      value: 'nao_obrigado',
    }),
    action: '',
    url: '',
  });
};

// `shouldNavigate` só é true no canal App: lá o parent nativo depende do
// `action:'navigate'`/`url` pra navegar. No Web/Captive Portal a navegação já
// é resolvida por `window.open` — mandar esse campo também nesses canais faz
// o frame pai da TIM (que hospeda nossa aplicação) reagir e redirecionar a
// própria aba atual, duplicando a navegação (bug relatado pela TIM).
export const trackAnalyticsUpsellControlAccept = (
  url: string,
  shouldNavigate: boolean,
  status: 'sucesso' | 'falha' = 'sucesso',
  error = '',
  errorDetail = '',
) => {
  addEventToGa4({
    ...buildGa4Event({
      type: 'click',
      location: 'recarga_bemobi',
      label: UPSELL_CONTROL_GA4_LABEL,
      value: 'contratar',
      status,
      error,
      errorDetail,
    }),
    action: shouldNavigate ? 'navigate' : '',
    url: shouldNavigate ? url : '',
  });
};

export const trackAnalyticsChoosePayment = (
  rechargeValue: unknown,
  location?: string,
) => {
  addEventToDatalayer({
    event: 'forma_pagamento',
    ...productDataGTM(rechargeValue),
    comm: 'Formas de pagamento',
  });
  if (location) {
    addEventToGa4(
      buildGa4Event({ type: 'view', location, label: 'forma_pagamento' }),
    );
  }
  addEventToLoop(
    buildLoopEvent({
      type: 'pageview',
      pageName: location ? 'CHECKOUT_PAGE' : 'PAYMENT_PAGE',
    }),
  );
};

// GTM do clique em Pagar no checkout — `add_payment_info` do mapa técnico da
// Cadastra (bemobi.recarga.04). O `index` é a posição da face paga na lista da
// valores-recarga, gravada junto com ela; o `tipo-de-recarga` é sempre
// `recarga-avulsa`, porque todo pagamento pelo checkout nasce numa face da lista
// (repetir e recomendada não passam por ele). Decisões na TPC-3835.
const addPaymentInfoGtm = (
  rechargeValue: unknown,
  paymentType: string,
  programadaAccepted: boolean,
) => {
  const { id, valor } = productDataGTM(rechargeValue);
  const productId = (rechargeValue as any)?.id ?? rechargeValue;
  const { selectedRechargeValueIndex: index } = useRechargeStore.getState();
  const forma = toKebabCase(formatPaymentMethod(paymentType));
  const programada = programadaAccepted
    ? 'programada-ativa'
    : 'programada-nao-ativa';
  const nome = `recarga-${valor}`;

  addEventToDatalayer({
    event: 'add_payment_info',
    items: [
      {
        item_id: id,
        item_name: nome,
        ...(index && { index }),
        price: valor,
        promotion_name: bonusSlug(productId),
        item_category: 'recarga',
      },
    ],
    currency: 'BRL',
    nome_do_produto: nome,
    value: valor,
    payment_type: forma,
    disparos: `recarga-forma-de-pagamento-${forma}-tipo-recarga-avulsa-${programada}`,
    status: 'pendente',
    etapa: 'recarga-forma-de-pagamento',
    elemento: 'botao',
    funcionalidade: 'recarga-bemobi',
    webview: 'recarga-bemobi',
    id_da_tag: 'bemobi.recarga.04',
  });
};

export const trackAnalyticsSelectPayment = (
  rechargeValue: unknown,
  paymentType: string,
  location?: string,
) => {
  // O aceite da programada chega no mesmo `payment_request` e é gravado na
  // store pelo listener antes desta chamada. Vale para o GA4 e o GTM do Pagar.
  const { recurrenceOptinStatus } = useCheckoutStore.getState();
  const programadaAccepted = isRecurrenceOptinAccepted(recurrenceOptinStatus);
  const isCheckoutPay = !!location && paymentType !== 'pix';

  // No Pagar do checkout, o `add_payment_info` SUBSTITUI o `selecionar_pagamento`
  // do mapa antigo: os dois no mesmo clique contariam pagamento a mais. Fora do
  // checkout e na chave Pix, segue o evento antigo.
  if (isCheckoutPay) {
    addPaymentInfoGtm(rechargeValue, paymentType, programadaAccepted);
  } else {
    addEventToDatalayer({
      event: 'selecionar_pagamento',
      ...productDataGTM(rechargeValue),
      forma: formatPaymentMethod(paymentType),
      comm: 'Escolha a forma de pagamento',
    });
  }
  if (location) {
    if (paymentType === 'pix') {
      addEventToGa4(
        buildGa4Event({ type: 'view', location, label: 'pix_copia_cola' }),
      );
    } else {
      // Clique em Pagar (slide 6). O meio de pagamento vai no sufixo do
      // `location`, montado pelo listener; o `value` é o nome do botão, que o
      // checkout não informa.
      const programada = programadaAccepted
        ? GA4_PROGRAMADA
        : GA4_NAO_PROGRAMADA;
      addEventToGa4(
        buildGa4Event({
          type: 'click',
          location,
          label: `forma_pagamento_${programada}`,
          value: 'pagar',
        }),
      );
    }
  }
  if (paymentType !== 'pix' || !location) {
    const amount = ((rechargeValue as any)?.id ?? (rechargeValue as any)) / 100;
    addEventToLoop(
      buildLoopEvent({
        type: 'click',
        pageName: location ? 'CHECKOUT_PAGE' : 'PAYMENT_PAGE',
        paymentMethod: paymentType,
        transactionAmount: amount,
      }),
    );
  }
};

export const trackAnalyticsConfirmationPage = (
  type: 'pageview' | 'click',
  paymentMethod?: string,
  valueInCents?: number,
) =>
  addEventToLoop(
    buildLoopEvent({
      type,
      pageName: 'CONFIRMATION_PAGE',
      paymentMethod,
      ...amountTag(valueInCents),
    }),
  );

export const trackAnalyticsFallbackView = (
  label: string,
  location: string,
  paymentMethod?: string,
  valueInCents?: number,
) => {
  addEventToGa4(
    buildGa4Event({
      type: 'view',
      location,
      label,
      status: 'falha',
      errorDetail: 'Erro ao concluir o pagamento',
    }),
  );
  // O aceite da sugestão não tem evento: o SDK não reporta esse clique.
  addEventToLoop(
    buildLoopEvent({
      type: 'pageview',
      pageName: 'CHECKOUT_FALLBACK',
      paymentMethod,
      ...amountTag(valueInCents),
    }),
  );
};

export const trackAnalyticsFallbackSuggestion = (
  label: string,
  location: string,
) => {
  const value =
    label === 'erro_fallback_pagar_com_pix'
      ? 'pagar_com_pix'
      : 'pagar_com_credito';
  addEventToGa4(buildGa4Event({ type: 'click', location, label, value }));
};

export const trackAnalyticsCheckoutPaymentResult = (
  rechargeValue: unknown,
  data: Record<string, unknown>,
) => {
  const status = (data.status as string)?.toLowerCase();
  const isSuccess = status === 'confirmed' || status === 'pending';
  const amount = ((rechargeValue as any)?.id ?? (rechargeValue as any)) / 100;
  addEventToLoop(
    buildLoopEvent({
      type: isSuccess ? 'conversion' : 'conversionerror',
      pageName: 'STATUS_PAGAMENTO',
      paymentMethod: data.payment_method as string,
      transactionAmount: amount,
    }),
  );
};

export const trackAnalyticsPixCopy = (
  location: string,
  rechargeValue: unknown,
) => {
  addEventToGa4(
    buildGa4Event({
      type: 'click',
      location,
      label: 'pix_copia_cola',
      value: 'copiar',
    }),
  );
  const amount = ((rechargeValue as any)?.id ?? (rechargeValue as any)) / 100;
  addEventToLoop(
    buildLoopEvent({
      type: 'click',
      pageName: 'CHECKOUT_PAGE',
      paymentMethod: 'pix',
      transactionAmount: amount,
    }),
  );
};

// GTM (dataLayer) da jornada Pix — tags 51 a 57. Sobem dentro das funções GA4
// já existentes, nos mesmos pontos de interação, e leem a store direto (como
// helpers.ts faz com session/channel/customer), então nenhuma tela precisa
// passar valor por parâmetro. Payloads na §7.2.1 do doc de tracking.
const PIX_SCREEN_CONTENT_TYPE = 'pague-a-recarga-via-pix';
const PIX_GUIDE_CONTENT_TYPE = 'como-pagar-via-pix';
const PIX_QRCODE_CONTENT_TYPE = 'escaneie-o-qr-code';
// Identificador da tela de Pix. O mapa usa o mesmo valor em dois campos: o
// `screen_name` da tag 51 e o `etapa` das 52/53/56/57 — daí o nome neutro.
const PIX_SCREEN_SLUG = 'recarga-confirmacao-via-pix';
const PIX_GUIDE_ETAPA = 'recarga-como-pagar';

// {classe-da-tela} não é definido no mapa: usamos o nome da rota.
const PIX_SCREEN_CLASS = 'pagamento-pix';

// Valor em reais da recarga exibida na tela: `rechargeResult` no fluxo normal,
// `pixRechargeValue` enquanto ele ainda não chegou na store.
const pixValorProduto = () => {
  const { rechargeResult, pixRechargeValue } = useRechargeStore.getState();
  return (rechargeResult?.value ?? pixRechargeValue ?? 0) / 100;
};

const pixSelectContent = ({
  contentType,
  etapa,
  disparos,
  elemento,
  idDaTag,
}: {
  contentType: string;
  etapa: string;
  disparos: string;
  elemento: 'pop-up' | 'botao';
  idDaTag: string;
}) =>
  addEventToDatalayer(
    buildSelectContent({
      contentType,
      etapa,
      disparos,
      elemento,
      idDaTag,
      valorProduto: pixValorProduto(),
    }),
  );

// O mapa divide os cliques em duas tags: .52 para ações sobre o código, .53
// para navegação e ajuda. O `perfil` da .53 não existe nesta tela. Chaves
// tipadas porque um botão fora do mapa sai sem disparar, em silêncio.
type PixScreenButton =
  | 'copiar'
  | 'copiar-codigo'
  | 'exibir-qr-code'
  | 'como-funciona'
  | 'fechar';

const PIX_SCREEN_BUTTON_TAG: Record<PixScreenButton, string> = {
  copiar: 'bemobi.recarga.52',
  'copiar-codigo': 'bemobi.recarga.52',
  'exibir-qr-code': 'bemobi.recarga.52',
  'como-funciona': 'bemobi.recarga.53',
  fechar: 'bemobi.recarga.53',
};

const trackPixScreenViewGtm = () => {
  addEventToDatalayer({
    event: 'screen_view',
    screen_name: PIX_SCREEN_SLUG,
    screen_class: PIX_SCREEN_CLASS,
    ...rechargeProductFields(pixValorProduto()),
    funcionalidade: 'recarga-bemobi',
    webview: 'recarga-bemobi',
    id_da_tag: 'bemobi.recarga.51',
  });
};

const trackPixScreenClickGtm = (botao: string) => {
  const button = toKebabCase(botao);
  const idDaTag = PIX_SCREEN_BUTTON_TAG[button as PixScreenButton];
  if (!idDaTag) return;
  pixSelectContent({
    contentType: PIX_SCREEN_CONTENT_TYPE,
    etapa: PIX_SCREEN_SLUG,
    disparos: `${PIX_SCREEN_SLUG}-${button}`,
    elemento: 'botao',
    idDaTag,
  });
};

// Tela /web/pagamento-pix (Pix non-checkout) — Mapa Tag-DM / Repetir Recarga.
// Location fixo: essa tela nunca passa pelo Checkout SDK/iframe.
const PAGAR_VIA_PIX_LOCATION = 'recarga_bemobi';
const PAGAR_VIA_PIX_LABEL = 'pagar_via_pix';

// A tela tem event-label próprio quando é aberta pelo modal de pré-portal
// (Mapa Tag-DM US Carrinho Abandonado). A origem é derivada aqui, não recebida
// da página: está exibindo o Pix abandonado se, e só se, o token na tela é o
// do Pix pendente — `qr_code` é único por transação. Quem recebe label do
// chamador (trackAnalyticsFallbackView, StatusView) recebe algo que só ele
// sabe; aqui seria estado de store que esta camada já lê.
const ABANDONED_CART_PIX_LABEL = 'carrinho_abandonado_codigo_pix_gerado';

const isPixFromAbandonedCart = () => {
  const { pixToken, pendingPixRecharge } = useRechargeStore.getState();
  return !!pixToken && pixToken === pendingPixRecharge?.qr_code;
};

const pagarViaPixLabel = () =>
  isPixFromAbandonedCart() ? ABANDONED_CART_PIX_LABEL : PAGAR_VIA_PIX_LABEL;

const pixScreenLoop = (type: LoopClickType | 'pageview') =>
  addEventToLoop(
    buildLoopEvent({
      type,
      pageName: 'PIX_PAGE',
      paymentMethod: 'pix',
      transactionAmount: pixValorProduto(),
    }),
  );

export const trackAnalyticsPagarViaPixView = () => {
  addEventToGa4(
    buildGa4Event({
      type: 'view',
      location: PAGAR_VIA_PIX_LOCATION,
      label: pagarViaPixLabel(),
    }),
  );
  trackPixScreenViewGtm();
  pixScreenLoop('pageview');
};

export const trackAnalyticsPagarViaPixClick = (botao: string) => {
  addEventToGa4(
    buildGa4Event({
      type: 'click',
      location: PAGAR_VIA_PIX_LOCATION,
      label: pagarViaPixLabel(),
      value: botao,
    }),
  );
  trackPixScreenClickGtm(botao);
  // A cópia sai do handleCopyPix da página, ponto único dos três caminhos.
  if (toKebabCase(botao) !== 'exibir-qr-code') return;
  pixScreenLoop('click_exibir_qr_code');
};

// Um evento para os três caminhos de cópia: importa se copiou, não por onde.
export const trackAnalyticsPixCodeCopied = () =>
  pixScreenLoop('click_copiar_codigo');

// Voltar da tela: seta do header no mobile e item raiz do breadcrumb no
// desktop compartilham este evento. O `event-value` é `voltar` — não
// `voltar_ao_menu_inicial`, que é o do voltar da tela de sucesso
// (trackAnalyticsSucessoBack). As duas telas mandam o mesmo `event-location`,
// então o par label+value é o único jeito de separar as métricas.
export const trackAnalyticsPagarViaPixBack = () => {
  addEventToGa4(
    buildGa4Event({
      type: 'click',
      location: PAGAR_VIA_PIX_LOCATION,
      label: pagarViaPixLabel(),
      value: 'voltar',
    }),
  );
  // No mapa o "sair da tela" é o `fechar` da .53: aqui, seta do header ou
  // raiz do breadcrumb.
  trackPixScreenClickGtm('fechar');
  pixScreenLoop('click_voltar');
};

// Categoria 6 no mapa da tela, mas 3 no fluxo de carrinho abandonado, que
// define categoria 3 para a tela inteira.
export const trackAnalyticsComoFuncionaClick = () => {
  const label = pagarViaPixLabel();
  addEventToGa4(
    buildGa4Event({
      type: 'click',
      location: PAGAR_VIA_PIX_LOCATION,
      label,
      value: 'como_funciona',
      ...(label === PAGAR_VIA_PIX_LABEL && { category: '6' }),
    }),
  );
  trackPixScreenClickGtm('como_funciona');
  pixScreenLoop('click_como_funciona');
};

const comoFuncionaModalLoop = (type: 'impression' | 'click') =>
  addEventToLoop(
    buildLoopEvent({
      type,
      pageName: 'COMO_FUNCIONA_MODAL',
      paymentMethod: 'pix',
      transactionAmount: pixValorProduto(),
    }),
  );

export const trackAnalyticsComoPagarViaPixView = () => {
  addEventToGa4(
    buildGa4Event({
      type: 'view',
      location: PAGAR_VIA_PIX_LOCATION,
      label: 'como_pagar_via_pix',
      category: '6',
    }),
  );
  pixSelectContent({
    contentType: PIX_GUIDE_CONTENT_TYPE,
    etapa: PIX_GUIDE_ETAPA,
    disparos: `${PIX_GUIDE_ETAPA}-via-pix`,
    elemento: 'pop-up',
    idDaTag: 'bemobi.recarga.54',
  });
  comoFuncionaModalLoop('impression');
};

export const trackAnalyticsComoPagarViaPixClick = () => {
  addEventToGa4(
    buildGa4Event({
      type: 'click',
      location: PAGAR_VIA_PIX_LOCATION,
      label: 'como_pagar_via_pix',
      value: 'entendi',
      category: '6',
    }),
  );
  pixSelectContent({
    contentType: PIX_GUIDE_CONTENT_TYPE,
    etapa: PIX_GUIDE_ETAPA,
    disparos: `${PIX_GUIDE_ETAPA}-via-pix-entendi`,
    elemento: 'botao',
    idDaTag: 'bemobi.recarga.55',
  });
  comoFuncionaModalLoop('click');
};

const qrCodeModalLoop = (
  type: 'impression' | 'click_qr_code' | 'click_negar',
) =>
  addEventToLoop(
    buildLoopEvent({
      type,
      pageName: 'QR_CODE_MODAL',
      paymentMethod: 'pix',
      transactionAmount: pixValorProduto(),
    }),
  );

export const trackAnalyticsQrCodeView = () => {
  addEventToGa4(
    buildGa4Event({
      type: 'view',
      location: PAGAR_VIA_PIX_LOCATION,
      label: 'QRCode',
      category: '6',
    }),
  );
  pixSelectContent({
    contentType: PIX_QRCODE_CONTENT_TYPE,
    etapa: PIX_SCREEN_SLUG,
    disparos: `${PIX_SCREEN_SLUG}-qr-code`,
    elemento: 'pop-up',
    idDaTag: 'bemobi.recarga.56',
  });
  qrCodeModalLoop('impression');
};

export const trackAnalyticsQrCodeClick = (botao: string) => {
  addEventToGa4(
    buildGa4Event({
      type: 'click',
      location: PAGAR_VIA_PIX_LOCATION,
      label: 'QRCode',
      value: botao,
      category: '6',
    }),
  );
  // O mapa só define dataLayer para o "Copiar código Pix"; o X fica sem tag,
  // como no popup de confirmação (49/50).
  const button = toKebabCase(botao);
  if (button === 'fechar') {
    qrCodeModalLoop('click_negar');
    return;
  }
  if (button !== 'copiar-codigo-pix') return;
  qrCodeModalLoop('click_qr_code');
  pixSelectContent({
    contentType: PIX_QRCODE_CONTENT_TYPE,
    etapa: PIX_SCREEN_SLUG,
    disparos: `${PIX_SCREEN_SLUG}-qr-code-${button}`,
    elemento: 'botao',
    idDaTag: 'bemobi.recarga.57',
  });
};

export const trackAnalyticsCompleteRecharge = (
  rechargeValue: unknown,
  correlationId: string,
  paymentType: string,
  pageName = 'RECARGA_AVULSA',
) => {
  addEventToDatalayer({
    event: 'conversao',
    pedido: correlationId,
    ...productDataGTM(rechargeValue),
    forma: formatPaymentMethod(paymentType),
  });
  const amount = ((rechargeValue as any)?.id ?? (rechargeValue as any)) / 100;
  addEventToLoop(
    buildLoopEvent({
      type: 'confirmation',
      pageName,
      paymentMethod: paymentType,
      transactionAmount: amount,
    }),
  );
};

export const trackAnalyticsInCompleteRecharge = (
  rechargeValue: unknown,
  paymentType: string,
  status: string,
  isError = true,
  pageName = 'RECARGA_AVULSA',
) => {
  addEventToDatalayer({
    event: 'feedback',
    status,
    ...productDataGTM(rechargeValue),
    forma: formatPaymentMethod(paymentType),
  });
  const amount = ((rechargeValue as any)?.id ?? (rechargeValue as any)) / 100;
  addEventToLoop(
    buildLoopEvent({
      type: isError ? 'confirmationerror' : 'confirmationprocessing',
      pageName,
      paymentMethod: paymentType,
      transactionAmount: amount,
    }),
  );
};

// Um page_name por estado; o `flow` separa os dois arquivos da mesma tela.
const STATUS_PAGE_NAME: Record<string, Record<string, string>> = {
  checkout: {
    loading: 'STATUS_CHECKOUT_PAGE',
    processing: 'STATUS_PROCESSING_CHECKOUT_PAGE',
    paymentError: 'STATUS_PAYMENT_ERROR_CHECKOUT_PAGE',
    benefitError: 'STATUS_BENEFIT_ERROR_CHECKOUT_PAGE',
  },
  avulsa: {
    loading: 'STATUS_PAGE',
    processing: 'STATUS_PROCESSING_PAGE',
    error: 'STATUS_ERROR_PAGE',
  },
};

// Segue a ordem do polling, não o primeiro campo preenchido: o
// pixRechargeValue só é limpo no logout. `lastRechargeValue` vem em reais.
export const avulsaStatusAmountInCents = ({
  pixTransactionId,
  pixRechargeValue,
  creditTransactionId,
  selectedRechargeValueId,
  lastRechargeValue,
}: {
  pixTransactionId?: string | null;
  pixRechargeValue?: number | null;
  creditTransactionId?: string | null;
  selectedRechargeValueId?: number | null;
  lastRechargeValue?: number | null;
}): number | undefined => {
  if (pixTransactionId) return pixRechargeValue ?? undefined;
  if (creditTransactionId) return selectedRechargeValueId ?? undefined;
  return lastRechargeValue ? lastRechargeValue * 100 : undefined;
};

export const trackAnalyticsStatusScreen = (
  flow: 'checkout' | 'avulsa',
  status: string,
  paymentMethod?: string,
  valueInCents?: number,
) => {
  const pageName = STATUS_PAGE_NAME[flow]?.[status];
  if (!pageName) return;
  addEventToLoop(
    buildLoopEvent({
      type: status === 'loading' ? 'pageview' : 'impression',
      pageName,
      paymentMethod,
      ...amountTag(valueInCents),
    }),
  );
};

export const trackAnalyticsStatusPixRetry = (valueInCents?: number) => {
  addEventToLoop(
    buildLoopEvent({
      type: 'click',
      pageName: 'STATUS_PAYMENT_ERROR_CHECKOUT_PAGE',
      paymentMethod: 'pix',
      ...amountTag(valueInCents),
    }),
  );
};

export const trackAnalyticsStatusView = (location: string, label: string) => {
  addEventToGa4(buildGa4Event({ type: 'view', location, label }));
};

export const trackAnalyticsStatusClick = (
  location: string,
  label: string,
  value: string,
) => {
  addEventToGa4(buildGa4Event({ type: 'click', location, label, value }));
};

// `rechargeResult.value` vem em centavos.
const sucessoLoop = (
  type: 'pageview' | 'click_voltar' | 'click_banner',
  paymentMethod?: string,
  valueInCents?: number,
) =>
  addEventToLoop(
    buildLoopEvent({
      type,
      pageName: 'SUCESSO_PAGAMENTO',
      paymentMethod,
      ...amountTag(valueInCents),
    }),
  );

export const trackAnalyticsSucessoView = (
  location: string,
  paymentMethod?: string,
  valueInCents?: number,
) => {
  addEventToGa4(
    buildGa4Event({
      type: 'view',
      location,
      label: sucessoGa4Label(),
    }),
  );
  sucessoLoop('pageview', paymentMethod, valueInCents);
};

export const trackAnalyticsSucessoBack = (
  location: string,
  paymentMethod?: string,
  valueInCents?: number,
) => {
  addEventToGa4(
    buildGa4Event({
      type: 'click',
      location,
      label: sucessoGa4Label(),
      value: 'voltar_ao_menu_inicial',
    }),
  );
  sucessoLoop('click_voltar', paymentMethod, valueInCents);
};

// Se o card for para outra tela, o page_name precisa virar parâmetro.
export const trackAnalyticsRechargaPremiadaClick = (
  paymentMethod?: string,
  valueInCents?: number,
) => {
  addEventToDatalayer({
    event: 'oferta_recarga_premiada',
    botao: 'ir_para_recarga_premiada',
  });
  sucessoLoop('click_banner', paymentMethod, valueInCents);
};
